import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';

// Interface for set info returned by the public API
export interface PublicSetInfo {
  code: string;
  name: string;
  cardCount: number;
  iconUrl: string | null;
  releasedAt: string | null;
  setType: string | null;
}

// Interface for card info returned by the set detail API (paginated endpoint)
export interface PublicSetCard {
  id: number;
  scryfallID: string;
  name: string;
  collectorNumber: string;
  rarity: string;
  imageUrl: string | null;
}

// Interface for card info returned by the /all endpoint (SEO-optimized)
export interface PublicSetCardAll {
  scryfallID: string;
  name: string;
  collectorNumber: string;
  rarity: string;
  imageUris: {
    small: string | null;
    normal: string | null;
  };
  manaCost: string | null;
  typeLine: string | null;
  pointValue: number | null;
  usd: number | null;
  usdFoil: number | null;
  // Optional until the backend extension of /public/sets/:setCode/all ships;
  // the list view renders without these detail lines when absent.
  oracleText?: string | null;
  flavorText?: string | null;
  power?: string | null; // string, not number: "*", "1+*" exist
  toughness?: string | null;
}

export interface PublicSetStats {
  setCode: string;
  cardsListed: number;
  cardsWanted: number;
  activeUsersCount: number;
}

export interface PublicSetRelatedResponse {
  related: PublicSetInfo[];
}

// Response from GET /api/v1/public/sets
export interface PublicSetsListResponse {
  sets: PublicSetInfo[];
  totalCount: number;
}

// Response from GET /api/v1/public/sets/:setCode
export interface PublicSetDetailResponse {
  set: PublicSetInfo;
  cards: PublicSetCard[];
  totalCards: number;
  hasMore: boolean;
}

// Request params for set detail
export interface PublicSetDetailRequest {
  setCode: string;
  page?: number;
  limit?: number;
}

// Response from GET /api/v1/public/sets/:setCode/all
export interface PublicSetAllCardsResponse {
  set: PublicSetInfo;
  cards: PublicSetCardAll[];
  totalCards: number;
}

export const publicSetsApi = createApi({
  reducerPath: 'publicSetsApi',
  baseQuery: baseQuery,
  tagTypes: ['PublicSets'],
  endpoints: (builder) => ({
    // Get all sets for the directory page (load all for client-side filtering)
    // Backend limits to 500 per request, so we fetch in batches and combine
    getPublicSets: builder.query<PublicSetsListResponse, void>({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        // Fetch first batch (0-499)
        const firstBatch = await fetchWithBQ({
          url: '/public/sets?limit=500&offset=0',
          method: 'GET',
          requiresAuth: false,
        } as any);

        if (firstBatch.error) {
          return { error: firstBatch.error };
        }

        const firstData = firstBatch.data as PublicSetsListResponse;
        const totalCount = firstData.totalCount;

        // If we have all sets in first batch, return immediately
        if (firstData.sets.length >= totalCount) {
          return { data: firstData };
        }

        // Fetch second batch (500-999)
        const secondBatch = await fetchWithBQ({
          url: '/public/sets?limit=500&offset=500',
          method: 'GET',
          requiresAuth: false,
        } as any);

        if (secondBatch.error) {
          return { error: secondBatch.error };
        }

        const secondData = secondBatch.data as PublicSetsListResponse;

        // Combine both batches
        const combinedSets = [...firstData.sets, ...secondData.sets];

        return {
          data: {
            sets: combinedSets,
            totalCount: totalCount,
          },
        };
      },
      providesTags: ['PublicSets'],
    }),

    // Get set detail with paginated cards
    getPublicSetDetail: builder.query<PublicSetDetailResponse, PublicSetDetailRequest>({
      query: ({ setCode, page = 1, limit = 50 }) => {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        });
        return {
          url: `/public/sets/${setCode.toLowerCase()}?${params.toString()}`,
          method: 'GET',
          requiresAuth: false,
        };
      },
      // Ensure each page has its own cache entry
      serializeQueryArgs: ({ queryArgs }) => {
        const { setCode, page = 1, limit = 50 } = queryArgs;
        return `${setCode.toLowerCase()}-page-${page}-limit-${limit}`;
      },
      providesTags: (result, error, { setCode, page = 1 }) => [
        { type: 'PublicSets', id: `${setCode.toLowerCase()}-page-${page}` },
      ],
    }),

    // Get all cards in a set (no pagination) for SEO-optimized table view
    getPublicSetAllCards: builder.query<PublicSetAllCardsResponse, string>({
      query: (setCode) => ({
        url: `/public/sets/${setCode.toLowerCase()}/all`,
        method: 'GET',
        requiresAuth: false,
      }),
      providesTags: (result, error, setCode) => [
        { type: 'PublicSets', id: `${setCode.toLowerCase()}-all` },
      ],
    }),

    // Aggregate TradeMagic activity counts for a set (listings/wants/active users)
    getPublicSetStats: builder.query<PublicSetStats, string>({
      query: (setCode) => ({
        url: `/public/sets/${setCode.toLowerCase()}/stats`,
        method: 'GET',
        requiresAuth: false,
      }),
      providesTags: (result, error, setCode) => [
        { type: 'PublicSets', id: `${setCode.toLowerCase()}-stats` },
      ],
    }),

    // Related sets (block-anchored first, then setType ±2y window)
    getPublicSetRelated: builder.query<PublicSetRelatedResponse, string>({
      query: (setCode) => ({
        url: `/public/sets/${setCode.toLowerCase()}/related`,
        method: 'GET',
        requiresAuth: false,
      }),
      providesTags: (result, error, setCode) => [
        { type: 'PublicSets', id: `${setCode.toLowerCase()}-related` },
      ],
    }),
  }),
});

export const {
  useGetPublicSetsQuery,
  useGetPublicSetDetailQuery,
  useGetPublicSetAllCardsQuery,
  useGetPublicSetStatsQuery,
  useGetPublicSetRelatedQuery,
} = publicSetsApi;
